<template>
    <DCard padding>
        <DCol>
            <DRow
                class="mb-3 d-flex justify-content-end"
                align-vertical="end"
            >
                <DCol
                    sm="2"
                >
                    <DButton
                        id="table-configuration"
                        icon="cog-outline"
                        :label="__('messages.list_configuration')"
                        class="float-end"
                        @click="showListViewModal = true"
                    />
                </DCol>
            </DRow>
            <DRow
                class="mb-3"
                align-vertical="end"
            >
                <DCol
                    sm="2"
                    class="pt-1"
                >
                    <DDateFormatInput
                        id="date_from"
                        v-model="filtersData.date_from"
                        format="YYYY-MM-DD"
                        :have-class="'mb-0'"
                        :label="__('tools.activity_logs.filters.date_from')"
                        :placeholder="'Select date'"
                        class="mb-0"
                        type="date"
                        @keydown.enter.exact.prevent="applyFilters"
                    />
                </DCol>
                <DCol
                    sm="2"
                    class="pt-1"
                >
                    <DDateFormatInput
                        id="date_to"
                        v-model="filtersData.date_to"
                        format="YYYY-MM-DD"
                        :have-class="'mb-0'"
                        :label="__('tools.activity_logs.filters.date_to')"
                        :placeholder="'Select date'"
                        class="mb-0"
                        type="date"
                        @keydown.enter.exact.prevent="applyFilters"
                    />
                </DCol>
                <DCol
                    sm="2"
                    class="pt-1"
                >
                    <DSelect
                        id="subject-dropdown"
                        v-model="filtersData.subjectType"
                        :label="__('tools.activity_logs.filters.subject')"
                        :options="subjectTypesOptions"
                    />
                </DCol>
                <DCol
                    sm="2"
                    class="pt-1"
                >
                    <DInput
                        id="subject-id"
                        v-model="filtersData.subjectId"
                        :label="__('tools.activity_logs.filters.subject_id')"
                        class="mb-0"
                        :invalid-feedback="subjectIdWithoutTypeError
                            ? __('tools.activity_logs.filters.subject_id_needs_subject')
                            : null"
                        @keydown.enter.exact.prevent="applyFilters"
                    />
                </DCol>
                <DCol
                    sm="2"
                    class="pt-1"
                >
                    <DInput
                        id="user-search"
                        v-model="filtersData.user"
                        :label="__('tools.activity_logs.filters.user_search')"
                        class="mb-0"
                    />
                </DCol>
                <DCol
                    sm="2"
                    class="pt-1"
                >
                    <DInput
                        id="search"
                        v-model="filtersData.term"
                        :label="__('tools.activity_logs.filters.search')"
                        class="mb-0"
                        @keydown.enter.exact.prevent="applyFilters"
                    />
                </DCol>
                <DCol
                    sm="2"
                    class="d-flex gap-2"
                >
                    <DButton
                        id="filter-apply"
                        :label="__('tools.activity_logs.filters.filter')"
                        @click="applyFilters"
                    />
                    <DButton
                        id="filter-reset"
                        icon="filter-off"
                        :label="__('messages.actions.reset_filter')"
                        @click="resetFilters"
                    />
                </DCol>
            </DRow>
        </DCol>
        <DTable
            :items="activityLogs.data"
            :fields="getFields"
            class="table-ellipsis-text mt-3"
        >
            <template #description="{item}">
                <td>
                    {{ $page.props.auth.user.language_code === 'de' ? item.description : item.description_en }}
                </td>
            </template>
            <template #customer="{item}">
                <td v-if="item.customer_id === null">
                    {{ __('tools.activity_logs.table.no-customer') }}
                </td>
                <td v-else-if="customerLinkable()">
                    <Link
                        :href="customerLink(item)"
                    >
                        {{ customerText(item) }}
                    </Link>
                </td>
                <td v-else>
                    {{ customerText(item) }}
                </td>
            </template>
            <template #subject="{item}">
                <td v-if="subjectLinkable(item)">
                    <Link
                        :href="subjectLink(item)"
                    >
                        {{ subjectText(item) }}
                    </Link>
                </td>
                <td v-else>
                    {{ subjectText(item) }}
                </td>
            </template>
            <template #user="{item}">
                <td v-if="item.user.display_name">
                    {{ item.user.display_name }} ({{ item.user.name }})
                </td>
                <td v-else>
                    {{ item.user.name }}
                </td>
            </template>
            <template #comment="{item}">
                <td>
                    {{ item.comment === null ? '' : item.comment }}
                </td>
            </template>
            <template #date="{item}">
                <td>
                    {{ item.created_at }}
                </td>
            </template>
            <template #request_id="{item}">
                <td>
                    {{ item.request_id === null ? '' : item.request_id }}
                </td>
            </template>
            <template #action="{item}">
                <td v-if="hasActivityLogChanges(item)">
                    <DButton
                        icon="eye"
                        mini
                        @click="viewChanges(item, subjectText(item))"
                    />
                </td>
                <td v-else />
            </template>
        </DTable>
        <SimplePagination
            v-if="activityLogs.prev_page_url !== null || activityLogs.next_page_url !== null"
            :current-page="activityLogs.current_page"
            :has-previous="activityLogs.prev_page_url !== null"
            :has-next="activityLogs.next_page_url !== null"
            @update:activePage="changePage"
        />
        <ChangesModal
            v-if="showChangesModal"
            :show-modal="showChangesModal"
            :changes="changes"
            :subject="subject"
            @close-modal="showChangesModal = false"
        />
        <ListViewModal
            v-if="showListViewModal"
            :show-modal="showListViewModal"
            :user-list-view="userListView"
            @close-modal="showListViewModal = false"
        />
    </DCard>
</template>

<script>
import _ from 'lodash'
import ChangesModal from '~@/Components/Common/ActivityLogs/ChangesModal.vue'
import SimplePagination from '~@/Components/Common/ActivityLogs/SimplePagination.vue'
import ListViewModal from '~@/Components/ListView/ListViewModal.vue'
import { activityLogChanges, hasActivityLogChanges } from '~@/Composables/activityLogChanges'

const subjectMapping = {
    Customer: {
        permission: 'customer.view',
        route: 'customers.customers.show',
        parameters: {
            // route parameter name: property name in item
            customer: 'customer_id'
        }
    },
    Invoice: {
        permission: 'invoice.view',
        route: 'customers.customers.invoices.show',
        parameters: {
            customer: 'customer_id',
            invoice: 'subject_id'
        }
    },
    CreditNote: {
        permission: 'credit_note.manage',
        route: 'customers.customers.credit-notes.show',
        parameters: {
            customer: 'customer_id',
            credit_note: 'subject_id'
        }
    },
    Contract: {
        permission: 'contract.view',
        route: 'customers.customers.contracts.index',
        parameters: {
            customer: 'customer_id',
            contract: 'subject_id'
        }
    },
    Contact: {
        permission: 'contact.view',
        route: 'customers.customers.contacts.show',
        parameters: {
            customer: 'customer_id',
            contact: 'subject_id'
        }
    },
    CustomerFile: {
        permission: 'customer.view',
        route: 'customers.customers.customer-files.index',
        parameters: {
            customer: 'customer_id'
        }
    },
    CustomerNote: {
        permission: 'customer.view',
        route: 'customers.customers.customer-notes.index',
        parameters: {
            customer: 'customer_id'
        }
    },
    ContractPhase: {
        permission: 'contract.view',
        route: 'customers.customers.contracts.index',
        parameters: {
            customer: 'customer_id',
            contract: 'contract_phase.contract_id'
        }
    },
    Termination: {
        permission: 'contract.view',
        route: 'customers.customers.contracts.index',
        parameters: {
            customer: 'customer_id',
            contract: 'termination.contract_id'
        }
    },
    Task: {
        permission: 'task.manage',
        route: 'tasks.tasks.index',
        parameters: {
            id: 'subject_id'
        }
    },
    Order: {
        permission: 'order.view',
        route: 'orders.',
        parameters: {
            orderId: 'subject_id'
        }
    },
    User: {
        permission: 'user.view',
        route: 'users.show',
        parameters: {
            user: 'subject_id'
        }
    },
    QueueJobRequest: {
        permission: 'contract.fulfillment',
        route: 'contracts.domains',
        parameters: {
            customerId: 'queue_job_request.job_reference.customer_id'
        }
    },
    Product: {
        permission: 'product.view',
        route: 'products.products.show',
        parameters: {
            product: 'subject_id'
        }
    },
    FulfillmentAccount: {
        permission: 'fulfillment.view',
        route: 'products.accounts.show',
        parameters: {
            account: 'subject_id'
        }
    },
    BusinessIncident: {
        permission: 'customer.incidents_view',
        route: 'customers.customers.business-incidents.edit',
        parameters: {
            business_incident: 'subject_id',
            customer: 'customer_id'
        }
    },
    TaxRule: {
        permission: 'setting.tax_rules',
        route: 'settings.tax-rules.index',
        parameters: {}
    },
    TenantSettings: {
        permission: 'setting.general',
        route: 'settings.tenant-settings.create',
        parameters: {}
    },
    CompanyInformation: {
        permission: 'setting.general',
        route: 'settings.company-information.create',
        parameters: {}
    },
    Brand: {
        permission: 'setting.brands',
        route: 'settings.brands.edit',
        parameters: {
            brand: 'subject_id'
        }
    },
    BankAccount: {
        permission: 'setting.bank_accounts',
        route: 'settings.bank-accounts.edit',
        parameters: {
            bankAccount: 'subject_id'
        }
    },
    CreditNoteType: {
        permission: 'setting.credit_note_types',
        route: 'settings.credit-note-types.edit',
        parameters: {
            credit_note_type: 'subject_id'
        }
    },
    MarketRegion: {
        permission: 'market_region.edit',
        route: 'settings.market-regions.edit',
        parameters: {
            market_region: 'subject_id'
        }
    },
    ReminderSetting: {
        permission: 'setting.reminder',
        route: 'settings.dunning-settings.create',
        parameters: {}
    },
    DunningFeesAndLimits: {
        permission: 'setting.reminder',
        route: 'settings.dunning-settings.fees-and-limits.edit',
        parameters: {
            marketRegion: 'properties.marketRegionId'
        }
    },
    TaxExemption: {
        permission: 'setting.tax_exemptions',
        route: 'settings.tax-exemption.index',
        parameters: {}
    },
    TaxRulesCreditNotes: {
        permission: 'setting.tax_rules_credit_note',
        route: 'settings.tax-rules-credit-notes.index',
        parameters: {}
    },
    MailTemplate: {
        permission: 'tool.emails',
        route: 'tools.emails.editor.edit',
        parameters: {
            brand: 'properties.brand_id',
            language_code: 'properties.language',
            emailType: 'properties.name'
        }
    },
    InvoicePdfSetting: {
        permission: 'setting.invoice_pdf',
        route: 'settings.invoice-pdf-settings.edit',
        parameters: {
            brand_id: 'properties.brand_id',
            language_code: 'properties.language'
        }
    },
    DunningPdfSetting: {
        permission: 'setting.dunning_pdf',
        route: 'settings.dunning-pdf-settings.edit',
        parameters: {
            brand_id: 'properties.brand_id',
            language_code: 'properties.language'
        }
    },
    InvoiceItem: {
        permission: 'invoice.view',
        route: 'customers.customers.invoices.show',
        parameters: {
            customer: 'invoice_item.invoice.customer_id',
            invoice: 'invoice_item.invoice.id'
        }
    },
    CronJob: {
        permission: 'admin.cronjobs',
        route: 'administrations.cron-jobs.edit',
        parameters: {
            cron_job: 'subject_id'
        }
    }
}

export default {
    name: 'ActivityLogsTable',
    components: { ListViewModal, ChangesModal, SimplePagination },
    props: {
        activityLogs: {
            type: Object,
            default: () => { return {} }
        },
        activityLogSubjects: {
            type: Array,
            default: () => { return [] }
        },
        filters: {
            type: Object,
            default: () => { return {} }
        },
        userListView: {
            type: Array,
            default: () => {
                return []
            }
        },
    },
    emits: ['changePage', 'resetFilters'],
    data () {
        return {
            subjectMapping,
            filtersData: _.cloneDeep(this.filters),
            showChangesModal: false,
            subjectIdWithoutTypeError: false,
            changes: {},
            subject: null,
            showListViewModal: false,
        }
    },
    computed: {
        subjectTypesOptions () {
            const subjects = this.activityLogSubjects.map(subjectType => {
                return {
                    value: subjectType.name,
                    label: subjectType.name
                }
            })

            subjects.unshift({
                value: '',
                label: ''
            })

            return subjects
        },
        userPermissions () {
            return this.$page.props.auth.permissions
        },
        getFields () {
            const availableList = []

            this.userListView.forEach((item) => {
                if (item.show_column === true) {
                    availableList.push({
                        key: item.column,
                        label: item.column !== 'actions' && item.column !== 'icon' ? this.__('settings.list_view.' + item.list + '.' + item.column) : '',
                        sorter: item.sort_column_enabled,
                        _style: item.width !== null ? { minWidth: item.width + 'px', maxWidth: item.width + 'px' } : {}
                    })
                }
            })

            return availableList
        },
    },
    methods: {
        hasActivityLogChanges,
        // A filter change always returns to page 1 and is the only thing that
        // persists the filter set. Turning a page must not re-save it.
        applyFilters () {
            if (this.filtersData.subjectId && !this.filtersData.subjectType) {
                this.subjectIdWithoutTypeError = true

                return
            }

            this.subjectIdWithoutTypeError = false
            this.$emit('changePage', 1, this.filtersData, true)
        },
        changePage (page) {
            this.$emit('changePage', page ?? 1, this.filtersData, false)
        },
        customerLinkable () {
            return this.userPermissions.includes('customer.view')
        },
        customerLink (item) {
            return this.$route('customers.customers.show', item.customer_id)
        },
        customerText (item) {
            let name = `${item.customer.firstname} ${item.customer.lastname}`

            if (item.customer.company) {
                name = item.customer.company
            }

            return `${name} (${item.customer_id})`
        },
        subjectLinkable (item) {
            // Rows logged before marketRegionId was recorded have no market region to link to.
            if (item.subject_type === 'App\\Models\\Settings\\DunningFeesAndLimits' &&
                (item.properties?.marketRegionId === undefined || item.properties?.marketRegionId === null)) {
                return false
            }

            return item.subject_model &&
                this.subjectMapping[item.subject_model.name] !== undefined &&
                this.areSubjectParamsSet(item, this.subjectMapping[item.subject_model.name].parameters) &&
                this.userPermissions.includes(this.subjectMapping[item.subject_model.name].permission)
        },
        subjectLink (item) {
            const subjectMappingElement = this.subjectMapping[item.subject_model.name]
            const routeParameters = {}

            for (const [key, value] of Object.entries(subjectMappingElement.parameters)) {
                const dataValue = this.getNestedValue(item, value) ?? 0

                if ((key === 'brand' || key === 'brand_id') && dataValue === 0) {
                    continue
                }

                routeParameters[key] = dataValue
            }

            if (subjectMappingElement.route === 'tasks.tasks.index') {
                routeParameters.statuses = ['open', 'done']
            }

            return this.$route(subjectMappingElement.route, routeParameters)
        },
        subjectText (item) {
            if (item.subject_model) {
                if (['MailTemplate', 'InvoicePdfSetting', 'DunningPdfSetting'].includes(item.subject_model.name)) {
                    return item.subject_model.name
                }

                // The subject is the per market region config row, whose id means nothing to the reader.
                if (item.subject_type === 'App\\Models\\Settings\\DunningFeesAndLimits') {
                    return `DunningFeesAndLimits (${item.properties?.marketRegionId ?? item.subject_id})`
                }

                if (item.subject_model.name === 'Cluster') {
                    const clusterId = item.properties?.clusterId

                    return clusterId && clusterId !== 'N/A'
                        ? `${item.subject_model.name} (${clusterId})`
                        : item.subject_model.name
                }

                return `${item.subject_model.name} (${item.subject_id})`
            }

            return item.subject_id
        },
        getNestedValue (obj, path) {
            const pathArray = path.split('.')
            let o = obj
            for (const p of pathArray) {
                o = o[p]
            }
            return o
        },
        resetFilters () {
            this.$emit('resetFilters', this.activityLogs.current_page)
        },
        viewChanges (item, subject) {
            this.changes = activityLogChanges(item)
            this.subject = subject
            this.showChangesModal = true
        },
        areSubjectParamsSet (item, params) {
            for (const key in params) {
                const pathArray = params[key].split('.')
                let selectedItem = item
                for (const path in pathArray) {
                    // A missing segment means there is nothing to link to, and would otherwise
                    // throw while walking the rest of the path.
                    if (selectedItem === null || selectedItem === undefined) {
                        return false
                    }

                    if (selectedItem[pathArray[path]] === null) {
                        return false
                    }

                    selectedItem = selectedItem[pathArray[path]]
                }
            }
            return true
        }
    }
}
</script>

<style lang="scss" scoped>
.button {
    background-color: rgb(26, 77, 128);
    color: white;
    font-size: 16px;
}
.tableText {
    text-align: center
}
</style>
